import { Table } from 'antd'
import React from 'react'

import { Box, Typography } from '@mui/material'
import { IUserOrderCancelledReport } from '../../../../../types/report'
import CircularLoading from '../../../../../components/CircularProgress'
import { EOrderStatus } from '../../../../../types/order'
import { Link, useNavigate } from 'react-router-dom'
import PageUrls from '../../../../../constants/page-urls'
import queryString from 'query-string'
import { IFilterMapped } from '../../../components/ReportFilter'
import Pagination from '../../../../../components/Pagination'
import { useUser } from '../../../../../hooks/useUser'
import { numberFormat } from '../../../../../utils'
import dayjs from 'dayjs'
import { ONLY_DATE_TIME_FORMAT } from '../../../../../constants/global'

interface IDailySalesTableProps {
  data: IUserOrderCancelledReport[] | undefined
  isLoading?: boolean
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  total: number
}

const CommissionReportTable = (props: IDailySalesTableProps) => {
  const { data, isLoading, filter, onFilterChange, total } = props
  const navigate = useNavigate()
  const onSelectUser = async (username: string) => {
    const query = queryString.stringify({
      actor: username,
      page: 1,
      perPage: 15,
      comission: username,
    })
    navigate(`${PageUrls.Orders.Index}/${EOrderStatus.PENDING}?${query}`, {
      replace: true,
    })
  }

  const { merchant } = useUser()

  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'คำสั่งซื้อ',
      dataIndex: 'orderCode',
      align: 'center' as const,
      key: 'orderCode',
      width: 30,
      render: (orderCode: string, order: IUserOrderCancelledReport) => {
        return (
          <>
            <Typography
              variant="subtitle2"
              color="text.primary"
              component={Link}
              to={`${PageUrls.Orders.Detail}/${order.order}`}
              target={'_blank'}
            >
              {orderCode}
            </Typography>
            <Typography
              sx={{ mb: 0, lineHeight: 1.1 }}
              fontSize={12}
              variant="subtitle2"
              color="text.secondary"
            >
              {dayjs(
                filter.dateType === 'orderDate'
                  ? order.orderDate
                  : order.createdAt,
              ).format(ONLY_DATE_TIME_FORMAT)}
            </Typography>
          </>
        )
      },
    },
    {
      title: 'ขายโดย',
      dataIndex: 'username',
      align: 'center' as const,
      key: 'username',
      width: 50,
      render: (username: string) => {
        return (
          <Typography variant="subtitle2" textAlign={'center'} minWidth={100}>
            {username}
          </Typography>
        )
      },
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      align: 'left' as const,
      key: 'productName',
      width: 150,
      render: (productName: string) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'left'}
          >
            {productName}
          </Typography>
        )
      },
    },
    {
      title: 'หมวดหมู่',
      dataIndex: 'categoryName',
      align: 'left' as const,
      key: 'categoryName',
      width: 80,
      render: (categoryName: string) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'left'}
          >
            {categoryName}
          </Typography>
        )
      },
    },
    {
      title: 'จำนวนชิ้น',
      dataIndex: 'quantity',
      align: 'center' as const,
      key: 'quantity',
      width: 75,
      render: (quantity: number) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {(quantity ?? 0).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'เซต',
      dataIndex: 'collection',
      key: 'collection',
      align: 'center' as const,
      width: 60,
      render: (collection: number) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {Number(collection ?? 0).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'เซต(ชิ้น)',
      dataIndex: 'collectionUnits',
      key: 'collectionUnits',
      align: 'center' as const,
      width: 60,
      render: (collectionUnits: number) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {Number(collectionUnits ?? 0).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'ฟรี(ชิ้น)',
      dataIndex: 'free',
      key: 'free',
      align: 'center' as const,
      width: 60,
      render: (free: number) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {Number(free ?? 0).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'COD',
      dataIndex: 'codTotal',
      key: 'codTotal',
      align: 'center' as const,
      width: 70,
      render: (codTotal: number) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {Number(codTotal ?? 0).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'ยอดโอน',
      dataIndex: 'bankTotal',
      align: 'center' as const,
      key: 'bankTotal',
      width: 70,
      render: (bankTotal: number) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {Number(bankTotal ?? 0).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'รวม',
      dataIndex: 'total',
      align: 'center' as const,
      key: 'total',
      width: 70,
      render: (_: any, data: IUserOrderCancelledReport) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {(
              Number(data?.bankTotal ?? 0) + Number(data?.codTotal ?? 0)
            ).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'ค่าคอม (ชิ้น)',
      dataIndex: 'commission',
      key: 'commission',
      align: 'center' as const,
      width: 70,
      render: (commission: number) => {
        return (
          <>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textAlign={'center'}
            >
              {Number(commission ?? 0).toLocaleString()}
            </Typography>
          </>
        )
      },
    },
    {
      title: 'ค่าคอม %',
      dataIndex: 'commissionPercent',
      key: 'commissionPercent',
      align: 'center' as const,
      width: 70,
      render: (commissionPercent: number, data: IUserOrderCancelledReport) => {
        return (
          <>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textAlign={'center'}
            >
              {Number(commissionPercent ?? 0).toLocaleString()}
            </Typography>
            <Typography
              variant="subtitle2"
              fontSize={11}
              color="#aaa"
              textAlign={'center'}
            >
              (
              {numberFormat(
                ((Number(data?.bankTotal ?? 0) + Number(data?.codTotal ?? 0)) *
                  (merchant.config?.commissionPercent ?? 0)) /
                  100,
              )}
              )
            </Typography>
          </>
        )
      },
    },
    {
      title: 'ค่าคอม (ORDER)',
      dataIndex: 'commissionOrder',
      key: 'commissionOrder',
      align: 'center' as const,
      width: 70,
      render: (commissionOrder: number) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {Number(commissionOrder ?? 0).toLocaleString()}
          </Typography>
        )
      },
    },
  ]

  return (
    <>
      <CircularLoading isLoading={isLoading} />
      <Table
        size={'middle'}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map(
          (commission: IUserOrderCancelledReport, index: number) => {
            return {
              ...commission,
              key: index,
              numberRow:
                index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
            }
          },
        )}
        pagination={false}
        bordered
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          onFilterChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default CommissionReportTable
